import React, { FunctionComponent, ReactElement, useEffect } from 'react';
import { Accordion, AccordionDetails, AccordionSummary } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import genericAccordionStyles from './genericAccordionStyles';
import clsx from 'clsx';

export interface IGenericAccordionProps {
  /**
   * Component that gets render in the header (summary) of accordion
   */
  summary: ReactElement;
  /**
   * Component that rendered in content (details) of accordion
   */
  details: ReactElement;
  /**
   * Value which determines if the accordion is opened by default
   */
  defaultExpanded?: boolean;
  /**
   * Boolean value used to modify accordion open state
   */
  expanded: boolean;
  /**
   * Method used to set the expended value
   */
  setExpanded: (expanded: boolean) => void;
  /**
   * Custom classes used to modify accordion style
   */
  classes?: {
    accordionPaper?: string;
    accordionSummary?: string;
    accordionDetails?: string;
    accordionSummaryContent?: string;
    expandIcon?: string;
  };
  dataTest?: string;
}

const GenericAccordion: FunctionComponent<IGenericAccordionProps> = ({
  summary,
  details,
  defaultExpanded = true,
  classes,
  expanded,
  setExpanded,
  dataTest,
}) => {
  const styles = genericAccordionStyles();

  useEffect(() => {
    if (defaultExpanded) {
      setExpanded(defaultExpanded);
    }
  }, []);

  return (
    <Accordion
      expanded={expanded}
      className={clsx([styles.accordionPaper, classes?.accordionPaper])}
      defaultExpanded={defaultExpanded}
      {...(dataTest ? { 'data-test': dataTest } : {})}
    >
      <AccordionSummary
        className={clsx([styles.accordionSummary, classes?.accordionSummary])}
        classes={{ expandIcon: classes?.expandIcon, content: classes?.accordionSummaryContent }}
        onClick={() => setExpanded(!expanded)}
        expandIcon={
          <ExpandMoreIcon
            className={styles.expandIcon}
            fontSize="small"
            {...(dataTest ? { 'data-test': `${dataTest}:expand_button` } : {})}
          />
        }
        {...(dataTest ? { 'data-test': `${dataTest}:summary` } : {})}
      >
        {summary}
      </AccordionSummary>
      <AccordionDetails className={clsx([styles.accordionDetails, classes?.accordionDetails])}>
        {details}
      </AccordionDetails>
    </Accordion>
  );
};

export default GenericAccordion;
